import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { GET_TRACKED_VEHICLES } from "../../../../../graphql/fleet/getTrackedVehiclesQuery";
import { GET_DEVICE_STATES } from "./realQueries";
import { IAppState } from "../../../../../store";
import { formatDistanceValue } from "../../../../../utils/distance";
import { Vehicle, VehicleStatus } from "./types";

interface TrackedVehicle {
  id: string;
  licencePlate: string;
  make?: string | null;
  model?: string | null;
  bodyType?: string | null;
  vin?: string | null;
  fuelType?: string | null;
  telemetryDeviceId?: string | null;
  teltonikaDeviceId?: string | null;
  immobiliser?: boolean | null;
  telematicCoreData?: {
    latitude?: number | null;
    longitude?: number | null;
    speed?: number | null;
    odometer?: number | null;
    eventTime?: string | null;
    address?: { street?: string | null; city?: string | null } | null;
  } | null;
  smartCarVehicleData?: {
    vehicleLocation?: { latitude?: number | null; longitude?: number | null } | null;
    odometer?: { distance?: number | null } | null;
    fuelLevel?: { percentRemaining?: number | null } | null;
  } | null;
}

interface RawDeviceState {
  deviceEsn: string;
  status?: "online" | "recently_active" | "offline" | "unknown" | null;
  signalLevel?: number | null;
  lastActive?: number | null;
}

function deriveLocation(v: TrackedVehicle): string {
  const street = v.telematicCoreData?.address?.street;
  const city = v.telematicCoreData?.address?.city;
  if (street && city) return `${street}, ${city}`;
  if (city) return city;
  if (street) return street;
  return "Unknown location";
}

// A business customer only ever has an active-booking-scoped handful of vehicles to track
// (see getTelematicsVehiclesForBusinessCustomer in inventory-api, which already joins
// BOOKING_SCHEDULE -> booking -> vehicle filtered to ACTIVE/IN_PROGRESS bookings for this
// customer) — unlike nuvven-b2b-app's fleet-wide useFleetVehicles, there's no 500k-vehicle
// scale concern here, so this fetches the whole (small) list in one shot: no offset/limit
// pagination, no separate lightweight counts poller.
export function useTrackedVehicles() {
  const user = useSelector((state: IAppState) => state.authReducer.user);

  const businessCustomerId = user?.businesscustomers?.id;

  const { data, loading, error, refetch: refetchVehicles } = useQuery<{ getTelematicsVehiclesForBusinessCustomer: TrackedVehicle[] }>(
    GET_TRACKED_VEHICLES,
    {
      variables: { businessCustomerId },
      skip: !businessCustomerId,
      fetchPolicy: "network-only",
    }
  );

  const rows = data?.getTelematicsVehiclesForBusinessCustomer ?? [];

  const deviceEsns = useMemo(
    () => Array.from(new Set(rows.map((v) => v.telemetryDeviceId).filter((esn): esn is string => !!esn))),
    [rows]
  );

  const deviceStates = useQuery<{ deviceStates: RawDeviceState[] }>(GET_DEVICE_STATES, {
    variables: { deviceEsn: deviceEsns },
    skip: deviceEsns.length === 0,
    fetchPolicy: "cache-and-network",
    pollInterval: 30000,
  });

  const vehicles = useMemo<Vehicle[]>(() => {
    const deviceStateByEsn = new Map((deviceStates.data?.deviceStates ?? []).map((s) => [s.deviceEsn, s]));

    const mapped = rows.map((v): Vehicle => {
      const lat = v.telematicCoreData?.latitude ?? v.smartCarVehicleData?.vehicleLocation?.latitude ?? null;
      const lng = v.telematicCoreData?.longitude ?? v.smartCarVehicleData?.vehicleLocation?.longitude ?? null;

      const speed = v.telematicCoreData?.speed ?? 0;
      const fuelPct = v.smartCarVehicleData?.fuelLevel?.percentRemaining;

      const deviceState = v.telemetryDeviceId ? deviceStateByEsn.get(v.telemetryDeviceId) : undefined;
      // Strictly "online" (connected===true) only — previously also treated
      // "recently_active" (disconnected, but within DEVICE_RECENTLY_ACTIVE_THRESHOLD_SECONDS
      // of its last message) as green/online, which reads as "live" in the UI (green dot,
      // animating car icon) for a vehicle that isn't actually connected right now.
      const status: VehicleStatus = deviceState?.status === "online" ? "online" : "offline";
      const signalLevel = deviceState?.signalLevel ?? null;
      const lastActive = deviceState?.lastActive ?? null;

      return {
        id: v.id,
        plate: v.licencePlate,
        make: v.make ?? "",
        model: v.model ?? "",
        type: v.bodyType ?? "",
        lat,
        lng,
        status,
        speedVal: speed,
        fuel: fuelPct != null ? Math.round(fuelPct) : null,
        location: deriveLocation(v),
        vin: v.vin ?? "",
        telemetryDeviceId: v.telemetryDeviceId ?? "",
        deviceId: v.teltonikaDeviceId ?? "",
        odometer: v.telematicCoreData?.odometer != null ? `${formatDistanceValue(v.telematicCoreData.odometer)} km` : "—",
        fuelType: v.fuelType ?? "",
        immobiliser: !!v.immobiliser,
        eventTime: v.telematicCoreData?.eventTime ?? null,
        signalLevel,
        lastActive,
      };
    });

    // Online-first, live: as deviceStates updates (poll every 30s, see above), a vehicle that
    // just came online should visually move up with the other online vehicles, and one that
    // just went offline should drop below them. Array.prototype.sort is stable (guaranteed
    // since ES2019), so this only reorders across the online/offline boundary — it doesn't
    // reshuffle relative order within either group on every re-render.
    return [...mapped].sort((a, b) => {
      if (a.status === b.status) return 0;
      return a.status === "online" ? -1 : 1;
    });
  }, [rows, deviceStates.data]);

  const totalCount = vehicles.length;
  const onlineCount = vehicles.filter((v) => v.status === "online").length;

  return {
    vehicles,
    totalCount,
    onlineCount,
    loading,
    error,
    refetch: () => {
      refetchVehicles();
      deviceStates.refetch();
    },
  };
}
